"use client"

import { CSSProperties, FormEvent, useEffect, useRef, useState } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"

import styles from "./footer.module.css"

const quickLinks = [
  { label: "Home", href: "/shop" },
  { label: "About Us", href: "/about-us" },
  { label: "Shipping", href: "/shipping" },
  { label: "Returns", href: "/returns" },
  { label: "Contact", href: "/contact" },
]

const legalLinks = [
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms of Service", href: "/terms-of-service" },
  { label: "Cookie Settings", href: "/cookie-settings" },
]

type RevealStyle = CSSProperties & {
  "--footer-delay": string
}

const revealDelay = (index: number): RevealStyle => ({
  "--footer-delay": `${index * 85}ms`,
})

function InstagramIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M7.03.08c-1.28.07-2.15.27-2.91.57-.79.3-1.46.72-2.12 1.38C1.33 2.7.92 3.37.62 4.16.32 4.93.12 5.8.06 7.08 0 8.35 0 8.76 0 12.02s.02 3.67.08 4.95c.06 1.28.27 2.15.57 2.91.3.79.72 1.46 1.39 2.12.67.67 1.33 1.08 2.13 1.38.76.3 1.63.5 2.91.56 1.27.06 1.69.07 4.94.06 3.26 0 3.67-.02 4.95-.08 1.28-.06 2.15-.27 2.91-.56.79-.31 1.46-.72 2.12-1.39.67-.67 1.08-1.34 1.38-2.13.3-.76.5-1.63.56-2.91.06-1.28.07-1.69.06-4.95 0-3.26-.02-3.67-.08-4.95-.06-1.28-.27-2.15-.56-2.91-.31-.79-.72-1.46-1.39-2.13C21.3 1.33 20.63.92 19.84.62 19.07.32 18.2.12 16.92.06 15.65 0 15.24 0 11.98 0S8.31.02 7.03.08Zm.14 21.7c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.42-2.23-.06-1.26-.07-1.64-.08-4.85 0-3.2.01-3.58.06-4.85.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.42 1.26-.06 1.64-.07 4.85-.08 3.2 0 3.58.01 4.85.06 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.42 2.23.06 1.27.07 1.65.08 4.85 0 3.2-.01 3.58-.06 4.85-.05 1.17-.25 1.81-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.42-1.27.06-1.65.07-4.85.08-3.2 0-3.58-.01-4.85-.06ZM16.95 5.59a1.44 1.44 0 1 0 2.88 0 1.44 1.44 0 0 0-2.88 0ZM5.84 12.01a6.16 6.16 0 1 0 12.32-.02 6.16 6.16 0 0 0-12.32.02ZM8 12.01a4 4 0 1 1 8-.02 4 4 0 0 1-8 .02Z" />
    </svg>
  )
}

function TiktokIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M12.53.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07Z" />
    </svg>
  )
}

function FacebookIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M13.45 24v-8.29h2.78l.42-3.23h-3.2v-2.06c0-.94.26-1.57 1.6-1.57h1.71V5.96a23 23 0 0 0-2.49-.13c-2.46 0-4.15 1.5-4.15 4.27v2.38H7.34v3.23h2.78V24h3.33Z" />
    </svg>
  )
}

function LinkedinIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.03-1.85-3.03s-2.14 1.44-2.14 2.93v5.67H9.35V9h3.42v1.56h.04c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12Zm1.78 13.02H3.55V9h3.57v11.45Z" />
    </svg>
  )
}

export default function Footer() {
  const footerRef = useRef<HTMLElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [subscribed, setSubscribed] = useState(false)

  useEffect(() => {
    const footer = footerRef.current

    if (!footer) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.12 }
    )

    observer.observe(footer)

    return () => observer.disconnect()
  }, [])

  const handleSubscribe = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubscribed(true)
  }

  return (
    <footer
      className={styles.footer}
      data-visible={isVisible}
      data-testid="storefront-footer"
      ref={footerRef}
    >
      <div className={styles.container}>
        <div className={styles.mainGrid}>
          <section className={styles.newsletter}>
            <h2
              className={styles.reveal}
              style={revealDelay(0)}
            >
              Stay Connected
            </h2>
            <p className={styles.reveal} style={revealDelay(1)}>
              Join our newsletter for the latest updates and exclusive offers.
            </p>

            <div className={styles.reveal} style={revealDelay(2)}>
              {subscribed ? (
                <p className={styles.confirmation} role="status">
                  Thank you for your trust
                </p>
              ) : (
                <form
                  className={styles.newsletterForm}
                  onSubmit={handleSubscribe}
                >
                  <label className={styles.srOnly} htmlFor="footer-email">
                    Email address
                  </label>
                  <input
                    autoComplete="email"
                    id="footer-email"
                    name="email"
                    placeholder="Enter your email"
                    required
                    type="email"
                  />
                  <button aria-label="Subscribe to newsletter" type="submit">
                    <svg aria-hidden="true" viewBox="0 0 24 24">
                      <path d="M5 12h13M13 7l5 5-5 5" />
                    </svg>
                  </button>
                </form>
              )}
            </div>
          </section>

          <nav aria-label="Quick links" className={styles.column}>
            <h2 className={styles.reveal} style={revealDelay(3)}>
              Quick Links
            </h2>
            <ul>
              {quickLinks.map((link, index) => (
                <li
                  className={styles.reveal}
                  key={link.href}
                  style={revealDelay(4 + index)}
                >
                  <LocalizedClientLink href={link.href}>
                    {link.label}
                  </LocalizedClientLink>
                </li>
              ))}
            </ul>
          </nav>

          <section className={styles.column}>
            <h2 className={styles.reveal} style={revealDelay(9)}>
              Contact Us
            </h2>
            <p className={styles.reveal} style={revealDelay(10)}>
              Questions about an order, a return, or our products?
            </p>
            <LocalizedClientLink
              className={`${styles.contactLink} ${styles.reveal}`}
              href="/contact"
              style={revealDelay(11)}
            >
              Get in touch
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M5 12h13M13 7l5 5-5 5" />
              </svg>
            </LocalizedClientLink>
          </section>

          <section className={styles.socials}>
            <h2 className={styles.reveal} style={revealDelay(12)}>
              Follow Us
            </h2>
            <div className={styles.socialList}>
              {[
                {
                  label: "Instagram",
                  href: "https://www.instagram.com",
                  icon: <InstagramIcon />,
                },
                {
                  label: "TikTok",
                  href: "https://www.tiktok.com",
                  icon: <TiktokIcon />,
                },
                {
                  label: "Facebook",
                  href: "https://www.facebook.com",
                  icon: <FacebookIcon />,
                },
                {
                  label: "LinkedIn",
                  href: "https://www.linkedin.com",
                  icon: <LinkedinIcon />,
                },
              ].map((social, index) => (
                <a
                  aria-label={social.label}
                  className={`${styles.socialLink} ${styles.reveal}`}
                  href={social.href}
                  key={social.label}
                  rel="noreferrer"
                  style={revealDelay(13 + index)}
                  target="_blank"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </section>
        </div>

        <div className={styles.bottomBar}>
          <p className={styles.reveal} style={revealDelay(17)}>
            © {new Date().getFullYear()} Exellious. All rights reserved.
          </p>
          <nav aria-label="Legal links">
            <ul>
              {legalLinks.map((link, index) => (
                <li
                  className={styles.reveal}
                  key={link.href}
                  style={revealDelay(18 + index)}
                >
                  <LocalizedClientLink href={link.href}>
                    {link.label}
                  </LocalizedClientLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  )
}
